import React, { PropsWithChildren, useEffect, useState } from "react";

interface Position {
    x: number;
    y: number;
}

interface DraggableWrapperProps {
    className?: string;
    initialStyle?: {
        top?: string;
        left?: string;
        transform?: string;
    };
}

const DraggableWrapper = ({
    children,
    className,
    initialStyle = {},
}: PropsWithChildren<DraggableWrapperProps>) => {
    const [position, setPosition] = useState<Position | null>(null);
    const [startPosition, setStartPosition] = useState<Position>({
        x: 0,
        y: 0,
    });
    const [dragging, setDragging] = useState(false);

    const handleDragStart = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const currentX = position?.x ?? rect.left;
        const currentY = position?.y ?? rect.top;

        setStartPosition({
            x: e.clientX - currentX,
            y: e.clientY - currentY,
        });
        setDragging(true);
    };

    const handleDrag = (e: MouseEvent) => {
        if (!dragging) return;
        setPosition({
            x: e.clientX - startPosition.x,
            y: e.clientY - startPosition.y,
        });
    };

    const handleDragEnd = () => {
        setDragging(false);
    };

    useEffect(() => {
        if (dragging) {
            window.addEventListener("mousemove", handleDrag);
            window.addEventListener("mouseup", handleDragEnd);
        } else {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleDragEnd);
        }

        return () => {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleDragEnd);
        };
    }, [dragging, startPosition]);

    const dynamicStyle = position
        ? {
              left: `${position.x}px`,
              top: `${position.y}px`,
              transform: "none",
          }
        : initialStyle;

    return (
        <span
            className={className}
            onMouseDown={handleDragStart}
            style={{
                position: "fixed",
                zIndex: Number.MAX_SAFE_INTEGER,
                cursor: dragging ? "grabbing" : "grab",
                ...dynamicStyle,
            }}
        >
            {children}
        </span>
    );
};

export default DraggableWrapper;
